import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import useCurrentGlobalSearchVerticalPage from 'hooks/GlobalSearch/useCurrentGlobalSearchVerticalPage'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import useGlobalSearchVerticalTabs from 'hooks/GlobalSearch/useGlobalSearchVerticalTabs'
import { preloadGlobalSearchV2Content } from 'components/SearchV2/GlobalSearchV2'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import Clickable from 'components/Common/Clickable'
import { rem } from 'polished'
import focusStyle from 'styles/utils/focusStyle'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import BodyText from 'LuxKit/Typography/BodyText'
import useIsSearchPage from 'hooks/Navigation/useIsSearchPage'
import { useLocation } from 'react-router-dom'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import { useIntl } from 'react-intl'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'

const FullSearchInput = styled(Clickable)`
  display: flex;
  border: 1px solid transparent;
  background-color: var(--palette-neutral-surface-light-background);
  border-radius: var(--border-radius-s);
  container-type: size;
  align-items: center;
  height: ${rem(40)};
  padding: ${rem(8)} ${rem(16)};
  transition: border-color 0.2s, opacity 0.2s;
  gap: ${rem(12)};
  width: 100%;
  opacity: 1;

  &[hidden] {
    pointer-events: none;
    opacity: 0;
  }

  ${mediaHoverable} {
    &:hover {
      border-color: var(--palette-neutral-surface-light-foreground);
    }
  }

  &:focus-visible {
    ${focusStyle}
  }

  body.${REBRAND_PALETTE_CLASS} & {
    border-radius: var(--border-radius-round);
    border-color: var(--palette-neutral-surface-light-foreground);
  }
`

const InputText = styled(BodyText)`
  @container (width >= 260px) {
    .short {
      display: none;
    }
  }

  @container (width < 260px) {
    .long {
      display: none;
    }
  }
`

interface Props {
  className?: string
  style?: React.CSSProperties
  ref?: React.RefObject<HTMLButtonElement | null>
  'data-agent-hub-onboarding'?: 'search'
}

function NavBarSearchInput(props: Props) {
  const { className, style, ref, 'data-agent-hub-onboarding': agentHubOnboardingStep } = props
  const location = useLocation()
  const globalSearchVerticalTabs = useGlobalSearchVerticalTabs()
  const currentSearchVerticalPage = useCurrentGlobalSearchVerticalPage()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const intl = useIntl()

  const isSearchPage = useIsSearchPage()

  const hideSearchBar = useMemo(() => {
    return isSearchPage &&
      !matchLocaleAwarePath(location.pathname, { path: ['/:regionCode/search/shop', '/:regionCode/search/transfer'] })
  }, [isSearchPage, location.pathname])

  const onSearchClick = useCallback(() => {
    const verticalTab = currentSearchVerticalPage ?? globalSearchVerticalTabs.keys().next().value
    if (verticalTab !== undefined) {
      toggleSearchVertical(verticalTab)
    }
  }, [currentSearchVerticalPage, globalSearchVerticalTabs, toggleSearchVertical])

  const onSearchIntent = useCallback(() => {
    preloadGlobalSearchV2Content()
  }, [])

  const searchLongText = intl.formatMessage({
    id: 'JI-uR9FUJ8KzSVaajNrz',
    defaultMessage: 'Search our exclusive travel offers',
  })

  const searchShortText = intl.formatMessage({
    id: 'ofmcoNPQ7KIkxgK-JL_e',
    defaultMessage: 'Search offers',
  })

  return <FullSearchInput
    className={className}
    {...(agentHubOnboardingStep ? { 'data-agent-hub-onboarding': agentHubOnboardingStep } : {})}
    onClick={onSearchClick}
    onMouseEnter={onSearchIntent}
    onFocus={onSearchIntent}
    ref={ref}
    style={style}
    hidden={hideSearchBar}
    aria-hidden={hideSearchBar}
  >
    <LineSearchIcon size="S" />
    <InputText variant="medium" colour="primary-dark" wrap="truncate">
      <span className="long">{searchLongText}</span>
      <span className="short">{searchShortText}</span>
    </InputText>
  </FullSearchInput>
}

export default React.memo(NavBarSearchInput)
